<template>
  <div ref="grid-container" class="grid-container-css">
    <LinkWrapper
      v-for="(item, index) in items"
      :key="item.id"
      :link="item.link"
      :width="item.width"
      :height="item.height"
      :show-bottom-bar="showBottomBar"
      @click="clickItem($event, item, shownListing, index)"
    >
      <Item :item="item" :border-radius="borderRadius" :lazy="index < 10 ? false : true">
        <slot :data="{ ...item, borderRadius }"></slot>
        <slot v-if="showBottomBar" name="bottomBar" :data="{ ...item, borderRadius }"></slot>
      </Item>
    </LinkWrapper>
  </div>
</template>

<script>
import LinkWrapper from './LinkWrapper';
import Item from './Item';
import { selectItemPayload, viewItemListPayload } from '@/utils/analytics/ecommerce';
export default {
  components: { LinkWrapper, Item },
  props: {
    items: {
      type: Array,
      required: true
    },
    borderRadius: {
      type: Number,
      required: false,
      default: 4
    },
    showBottomBar: {
      type: Boolean,
      required: false,
      default: false
    },
    sendVectorClick: {
      type: Boolean,
      required: false,
      default: false
    },
    /**
     * Identidad de la lista para `view_item_list` / `select_item` (VEX-6460).
     * Los dos eventos empujan EXACTAMENTE el mismo `item_list_id` e
     * `item_list_name`: es lo unico que le permite a GA4 unir la vista de la
     * grilla con el clic. Sin `list` la grilla no mide: mejor un hueco visible
     * que una lista anonima mezclada con las demas.
     */
    list: {
      type: Object,
      required: false,
      default: null
    }
  },
  data() {
    return {
      hiddenIds: []
    };
  },
  computed: {
    shownListing() {
      let vm = this;
      return this.items.filter((el) => {
        return !vm.hiddenIds.includes(el.id);
      });
    }
  },
  watch: {
    items() {
      this.loadVectorStats();
      this.fireGTMViewItemList();
    }
  },
  mounted() {
    this.loadVectorStats();
    this.fireGTMViewItemList();
  },
  methods: {
    loadVectorStats() {
      const isMyRequestsPage = this.$route.name.startsWith('profile-my-requests');
      const isProfile = this.$route.name.startsWith('profile');
      if (!isMyRequestsPage && !isProfile) {
        this.$store.dispatch(
          'user/LOAD_VECTORS_STATS',
          this.items.map((result) => result.id)
        );
      }
    },
    fireGTMViewItemList() {
      if (!this.list || !this.items || this.items.length === 0) return;
      const { data, ecommerce } = viewItemListPayload({
        contents: this.items,
        listId: this.list.id,
        listName: this.list.name
      });
      if (ecommerce.items.length === 0) return;
      this.$dataLayerPush('view_item_list', data, ecommerce);
    },
    fireGTMSelectItem(item, index) {
      if (!this.list) return;
      const { data, ecommerce } = selectItemPayload({
        content: item,
        // La posicion viene del v-for, no de un `indexOf`: buscar el objeto
        // devuelve -1 si la lista se reemplazo entre el render y el clic, y
        // -1 + 1 da 0, que se lee como una posicion valida en el informe.
        index: index + 1,
        listId: this.list.id,
        listName: this.list.name
      });
      // Un evento sin items no atribuye nada: ocupa una fila del informe y no
      // dice de que contenido habla.
      if (ecommerce.items.length === 0) return;
      this.$dataLayerPush('select_item', data, ecommerce);
    },
    clickItem($event, item, shownListing, index) {
      this.fireGTMSelectItem(item, index);
      if (this.sendVectorClick) {
        this.$store.dispatch('vectors/CLICK_VECTOR', { id: item.id });
      }
      if (item.isMaker) {
        const anchor = $event.target.closest('a');
        anchor.setAttribute('target', '_blank');
        this.$dataLayerPush(item.isMockup ? 'stock_mg_content' : 'stock_tm_content');
        return;
      }
      this.$emit('item-click', { item, shownListing }, $event);
    }
  }
};
</script>

<style lang="scss" scoped>
.grid-container-css {
  display: flex;
  flex-wrap: wrap;
  margin-right: -12px;
  margin-top: 5px;
}
.grid-container-css::after {
  --width: 2;
  --height: 1;
  --ratio: calc(var(--width) / var(--height));
  --row-height: 12rem;
  flex-basis: calc(var(--ratio) * var(--row-height));
  content: '';
  flex-grow: 1000000;
}
</style>
